import * as React from 'react'
import { useCallback, useEffect, useMemo } from 'react'

import { observer } from 'mobx-react'

import { IUploadLog } from '~/client/graph'
import NavBar from '~/client/src/desktop/components/NavBar/NavBar'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import AttachmentsStore from '~/client/src/desktop/views/Attachments/Attachments.store'
import DeliveryDetailsStore from '~/client/src/shared/components/DeliveryDetails/DeliveryDetails.store'
import * as Layout from '~/client/src/shared/components/Layout'
import Workflow from '~/client/src/shared/models/Workflow'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import DeliveriesStore from '~/client/src/shared/stores/domain/Deliveries.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import UploadLogConfigurationStore from '~/client/src/shared/stores/domain/UploadLogConfiguration.store'
import UploadLogsStore from '~/client/src/shared/stores/domain/UploadLogs.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import ProjectSetUp from '../ProjectSetUp/ProjectSetUp'
import AttachmentDialogs from './components/AttachmentDialogs/AttachmentDialogs'
import AttachmentModal from './components/AttachmentModal/AttachmentModal'
import AttachmentsHeaderBar from './components/AttachmentsHeaderBar/AttachmentsHeaderBar'
import AttachmentsList from './components/AttachmentsList/AttachmentsList'

import './Attachments.scss'

interface IProps {
  eventsStore?: DesktopEventStore
  uploadLogsStore?: UploadLogsStore
  uploadLogConfigurationStore?: UploadLogConfigurationStore
  companiesStore?: CompaniesStore
  projectMembersStore?: ProjectMembersStore
  userProjectsStore?: UserProjectsStore
  projectDateStore?: ProjectDateStore
  deliveriesStore?: DeliveriesStore
  state?: DesktopInitialState
  locationAttributesStore?: LocationAttributesStore
  workflowCategoriesStore?: WorkflowCategoriesStore
  deliveryDetailsStore?: DeliveryDetailsStore
  workflowTypesStore?: WorkflowTypesStore
  uploadLogsLoading?: boolean
  facetsLoading?: boolean
  totalFacetsLoading?: boolean
  allWorkflows?: Map<string, Workflow>
  viewableFacets?
  totalFacets?
  uploadLogs?: IUploadLog[]
  workflows?: Workflow[]
  setDatesFilter?: (datesFilter: {
    startDate?: number
    endDate?: number
  }) => void
}

const AttachmentsComponent: React.FC<IProps> = ({
  eventsStore,
  uploadLogsStore,
  uploadLogConfigurationStore,
  companiesStore,
  projectMembersStore,
  userProjectsStore,
  projectDateStore,
  deliveriesStore,
  state,
  locationAttributesStore,
  workflowCategoriesStore,
  deliveryDetailsStore,
  workflowTypesStore,
  uploadLogsLoading,
  facetsLoading,
  totalFacetsLoading,
  allWorkflows,
  viewableFacets,
  totalFacets,
  uploadLogs,
  workflows,
  setDatesFilter,
}) => {
  const store = useMemo(
    () =>
      new AttachmentsStore(
        eventsStore,
        uploadLogsStore,
        companiesStore,
        projectMembersStore,
        userProjectsStore,
        state,
        projectDateStore,
        deliveriesStore,
        uploadLogConfigurationStore,
        workflowCategoriesStore,
        deliveryDetailsStore,
        workflowTypesStore,
        locationAttributesStore,
      ),
    [
      eventsStore,
      uploadLogsStore,
      companiesStore,
      projectMembersStore,
      userProjectsStore,
      state,
      projectDateStore,
      deliveriesStore,
      uploadLogConfigurationStore,
      deliveryDetailsStore,
      workflowCategoriesStore,
      workflowTypesStore,
    ],
  )

  useEffect(() => {
    store.setFacets(viewableFacets)
  }, [store, viewableFacets])

  useEffect(() => {
    store.setTotalFacets(totalFacets)
  }, [store, totalFacets])

  useEffect(() => {
    store.initialize(uploadLogs)
    store.setDateFilterCallback(setDatesFilter)
    return () => {
      store.dispose()
    }
  }, [store, allWorkflows, uploadLogs])

  useEffect(() => {
    if (uploadLogs) {
      store.setUploadLogs(uploadLogs, allWorkflows)
    }
  }, [store, uploadLogs, allWorkflows])

  const renderView = useCallback((): JSX.Element => {
    return <AttachmentsList store={store} workflows={allWorkflows} />
  }, [store, allWorkflows])

  const renderAttachmentModal = useCallback(
    (modalWorkflows: Workflow[]): JSX.Element => {
      if (!store.isAttachmentModalOpen || !store.selectedAttachment) {
        return null
      }

      const fileName = store.getDisplayFileName(store.selectedAttachment)
      const fileType = store.selectedAttachment.fileType

      return (
        <AttachmentModal
          store={store}
          attachment={store.selectedAttachment}
          fileName={fileName}
          fileType={fileType}
          onClose={store.closeAttachmentModal}
          projectDateStore={projectDateStore}
          projectMembersStore={projectMembersStore}
          companiesStore={companiesStore}
          locationAttributesStore={locationAttributesStore}
          workflows={modalWorkflows}
        />
      )
    },
    [
      store,
      projectDateStore,
      projectMembersStore,
      companiesStore,
      locationAttributesStore,
      workflows,
    ],
  )
  const isLoading = store.isLoading || uploadLogsLoading || facetsLoading

  return (
    <Layout.View className="attachments">
      <Layout.Header className="layout-navigation">
        <NavBar />
      </Layout.Header>
      <Layout.View className="relative overflow-hidden">
        <div className="row full-height">
          <ProjectSetUp sidebarOnly className="col full-height w-fit-content" />
          <div className="col full-height">
            <AttachmentDialogs store={store} />
            {renderAttachmentModal(workflows)}
            <Layout.Header>
              <AttachmentsHeaderBar
                store={store}
                workflows={allWorkflows}
                viewableFacets={viewableFacets}
                totalFacets={totalFacets}
                isLoading={isLoading}
                totalFacetsLoading={totalFacetsLoading}
                facetsLoading={facetsLoading}
              />
            </Layout.Header>
            <Layout.Content>
              <div className="relative-block overflow-hidden multi-grid-container">
                <div className="full-height">{renderView()}</div>
              </div>
            </Layout.Content>
          </div>
        </div>
      </Layout.View>
      <Layout.Footer>
        <div />
      </Layout.Footer>
    </Layout.View>
  )
}

const Attachments = observer(AttachmentsComponent)

export default Attachments
